import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;
  const pollInterval = 2000;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`);
      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-testid="checkout-success-page">
      <Card className="card-base max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="h-16 w-16 mx-auto mb-6 rounded-full bg-forest/10 flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-forest animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-forest mb-2">
                Processing Your Payment
              </h1>
              <p className="text-gray-600">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="h-16 w-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-forest mb-2">
                Payment Successful!
              </h1>
              <p className="text-gray-600 mb-6">
                Thank you for your purchase. Your coaching package has been activated.
              </p>
              {paymentData && (
                <div className="bg-sand/50 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-gray-600">
                    Amount: <span className="font-semibold text-forest">
                      ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                    </span>
                  </p>
                </div>
              )}
              <div className="space-y-3">
                <Link to="/dashboard">
                  <Button className="w-full btn-primary">
                    Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/calendar">
                  <Button variant="outline" className="w-full btn-outline">
                    Schedule Your First Session
                  </Button>
                </Link>
              </div>
            </>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <>
              <div className="h-16 w-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-forest mb-2">
                {status === 'expired' ? 'Session Expired' : 'Payment Issue'}
              </h1>
              <p className="text-gray-600 mb-6">
                {status === 'expired' 
                  ? 'Your payment session has expired. Please try again.'
                  : status === 'timeout'
                  ? 'We couldn\'t confirm your payment status. Please check your email for confirmation.'
                  : 'Something went wrong. Please contact support if you were charged.'}
              </p>
              <div className="space-y-3">
                <Link to="/packages">
                  <Button className="w-full btn-primary">
                    Try Again
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="outline" className="w-full btn-outline">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckoutSuccessPage;
